import React, { lazy, Suspense } from 'react';
import { useSectionHeights } from "@/hooks/ui/useSectionHeights";
import AnimatedGradientBackground from "@/components/layout/animated-background";
import { HeroHeader, IntegrationCarouselBar } from "./sections";

const IndustryTrustSection = lazy(() => import("./sections/IndustryTrustSection").then(m => ({ default: m.IndustryTrustSection })));
const ChaosToTidySection = lazy(() => import("./sections/chaos-to-tidy").then(m => ({ default: m.ChaosToTidySection })));
const AIDemoSection = lazy(() => import("./sections/ai-demo").then(m => ({ default: m.AIDemoSection })));
const BentoGrid = lazy(() => import("./sections/BentoGrid").then(m => ({ default: m.BentoGrid })));
const MobileFeatureCarousel = lazy(() => import("./sections/MobileFeatureCarousel").then(m => ({ default: m.MobileFeatureCarousel })));
const SetupPinnedSection = lazy(() => import("./SetupSection").then(m => ({ default: m.SetupPinnedSection })));
const SetupSection = lazy(() => import("./SetupSection").then(m => ({ default: m.SetupSection })));
const FinalCTASection = lazy(() => import("./sections/FinalCTA").then(m => ({ default: m.FinalCTASection })));


const RedesignedHero = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  const {
    containerRef: bentoContainerRef,
    manageStartRef,
    growStartRef,
    businessStartRef,
    businessEndRef,
    managePercent,
    growPercent,
    businessPercent,
  } = useSectionHeights();

  return (
    <div className="relative bg-background text-foreground">
      {/* Hero Section */}
      <div id="hero" className="relative overflow-hidden" data-showable="hero">
        <AnimatedGradientBackground variant="hero" />

        <div className="relative z-10 flex flex-col min-h-main-content sm:min-h-main-content">
          <div className="flex-1 flex flex-col max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 w-full">
            <HeroHeader scrollToSection={scrollToSection} />
          </div>
          <div className="mt-auto">
            <IntegrationCarouselBar />
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        {/* Industry Trust — tabs + stats */}
        <IndustryTrustSection />

        {/* From Chaos to Clarity (scroll-driven animation) */}
        <ChaosToTidySection />

        {/* AI Demo Section (sticky scroll-driven tab switching) */}
        <AIDemoSection />

        {/* Enhanced Bento Box Grid - MANAGE, GROW, BUSINESS */}
        <div id="features" className="pt-8 sm:pt-12 pb-16 relative overflow-x-hidden bg-background" data-showable="features">
          <div
            className="absolute inset-0 pointer-events-none -z-10"
            style={{
              background: `linear-gradient(to bottom,
                hsl(var(--background)) 0%,
                rgba(var(--primary-rgb), 0.12) ${managePercent * 0.15}%,
                rgba(var(--primary-rgb), 0.12) ${managePercent * 0.7}%,
                rgba(165, 94, 78, 0.12) ${managePercent}%,
                rgba(var(--grow-rgb), 0.12) ${managePercent + growPercent * 0.3}%,
                rgba(var(--grow-rgb), 0.12) ${managePercent + growPercent * 0.7}%,
                rgba(139, 196, 130, 0.12) ${managePercent + growPercent}%,
                rgba(var(--secondary-rgb), 0.12) ${managePercent + growPercent + businessPercent * 0.3}%,
                rgba(var(--secondary-rgb), 0.12) ${managePercent + growPercent + businessPercent * 0.85}%,
                hsl(var(--background)) 100%
              )`
            }}
          />
          <div>
            <BentoGrid
              containerRef={bentoContainerRef}
              manageStartRef={manageStartRef}
              growStartRef={growStartRef}
              businessStartRef={businessStartRef}
              businessEndRef={businessEndRef}
            />
            <MobileFeatureCarousel />
          </div>
        </div>

        {/* Setup Section */}
        <div id="how-it-works" data-showable="how-it-works">
          <SetupPinnedSection />
          <SetupSection />
        </div>

        <FinalCTASection />
      </Suspense>

    </div>
  );
};

export default RedesignedHero;
